import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from './supabaseClient';
import { sendAccountActivityNotification } from './accountActivityNotification';
import {
  defaultAppSettings,
  saveAppSettings,
} from './appSettings';
import './App.css';

function getSubscriptionTierLabel(value) {
  const normalized = String(value || '')
    .trim()
    .toLowerCase();

  if (normalized === 'calendar-logbook-techlog') {
    return 'Calendar + logbooks';
  }

  if (normalized === 'full-package') {
    return 'Full package';
  }

  return 'Calendar booking';
}

export default function OwnerSetupPage({
  onOwnerCreated,
}) {
  const navigate = useNavigate();

  const [appSettings] = useState(defaultAppSettings);

  const [createdCode, setCreatedCode] =
    useState('');

  const [createdEmail, setCreatedEmail] =
    useState('');

  const [
    createdSubscriptionTier,
    setCreatedSubscriptionTier,
  ] = useState('');

  const [createdPassword, setCreatedPassword] =
    useState('');

  const [
    createdUserId,
    setCreatedUserId,
  ] = useState('');

  const [
    createdOrganisationId,
    setCreatedOrganisationId,
  ] = useState('');

  const [
    createdHomeAirfield,
    setCreatedHomeAirfield,
  ] = useState('');

  const [verifyMessage, setVerifyMessage] =
    useState('');

  const [loginLoading, setLoginLoading] =
    useState(false);

  const [form, setForm] = useState({
    orgName: '',
    fullName: '',
    email: '',
    password: '',
    confirmPassword: '',
    phone: '',
    homeAirfield: '',
    subscriptionTier: '',
    agree: false,
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [submitError, setSubmitError] =
    useState('');

  function validate() {
    const errs = {};

    if (!form.orgName.trim()) {
      errs.orgName =
        'Organisation name required';
    }

    if (!form.fullName.trim()) {
      errs.fullName = 'Full name required';
    }

    if (!form.email.trim()) {
      errs.email = 'Email required';
    }

    if (!form.password) {
      errs.password = 'Password required';
    } else if (form.password.length < 8) {
      errs.password =
        'Password must be at least 8 characters';
    }

    if (
      form.password !== form.confirmPassword
    ) {
      errs.confirmPassword =
        'Passwords do not match';
    }

    if (!form.phone.trim()) {
      errs.phone = 'Phone required';
    }

    if (!form.homeAirfield.trim()) {
      errs.homeAirfield =
        'Base airfield required';
    }

    if (!form.subscriptionTier) {
      errs.subscriptionTier =
        'Please choose a package';
    }

    if (!form.agree) {
      errs.agree =
        'You must agree to the terms';
    }

    setErrors(errs);

    return Object.keys(errs).length === 0;
  }

  function handleChange(event) {
    const {
      name,
      value,
      type,
      checked,
    } = event.target;

    const nextValue =
      type === 'checkbox'
        ? checked
        : name === 'homeAirfield'
          ? String(value || '').toUpperCase()
          : value;

    setForm((prev) => ({
      ...prev,
      [name]: nextValue,
    }));

    setSubmitError('');

    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: '',
      }));
    }
  }

  async function saveOrganisationPackage(
    accessToken,
    subscriptionTier
  ) {
    if (!accessToken) {
      throw new Error(
        'Could not authenticate the package selection.'
      );
    }

    const response = await fetch(
      '/.netlify/functions/billing-settings-save',
      {
        method: 'POST',
        headers: {
          'Content-Type':
            'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          subscriptionTier,
          startTrial: true,
        }),
      }
    );

    let payload = {};

    try {
      payload = await response.json();
    } catch (_error) {
      payload = {};
    }

    if (!response.ok) {
      throw new Error(
        payload.error ||
          'Could not save the organisation package.'
      );
    }
  }

  async function saveOrganisationBaseAirfield(
    organisationId,
    homeAirfield
  ) {
    const normalizedOrganisationId = String(
      organisationId || ''
    ).trim();
    const normalizedHomeAirfield = String(
      homeAirfield || ''
    )
      .trim()
      .toUpperCase();

    if (
      !normalizedOrganisationId ||
      !normalizedHomeAirfield
    ) {
      throw new Error(
        'Could not save the organisation base airfield.'
      );
    }

    const payload = {
      organisation_id:
        normalizedOrganisationId,
      name: normalizedHomeAirfield,
      icao: normalizedHomeAirfield,
      weather_icao:
        normalizedHomeAirfield,
      weather_source_name: null,
      notes: null,
      singleton: true,
    };

    const { error } = await supabase
      .from('airfields')
      .upsert([payload], {
        onConflict: 'organisation_id',
      });

    if (error) {
      throw new Error(
        `Could not save the organisation base airfield: ${error.message}`
      );
    }
  }

  async function handleSubmit(event) {
    event.preventDefault();
    setSubmitError('');

    if (!validate()) return;

    setLoading(true);

    try {
      const email = form.email
        .trim()
        .toLowerCase();

      const fullName =
        form.fullName.trim();

      const organisationName =
        form.orgName.trim();

      const phone = form.phone.trim();

      const homeAirfield = String(
        form.homeAirfield || ''
      )
        .trim()
        .toUpperCase();

      const response = await fetch(
        '/.netlify/functions/register-owner',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            email,
            password: form.password,
            fullName,
            organisationName,
            phone,
            homeAirfield,
            subscriptionTier:
              form.subscriptionTier,
          }),
        }
      );

      let payload;
      try {
        payload = await response.json();
      } catch (_error) {
        throw new Error(
          'The registration service returned an invalid response.'
        );
      }

      if (!response.ok) {
        throw new Error(
          payload?.error ||
            'The owner account could not be created.'
        );
      }

      if (
        !payload?.userId ||
        !payload?.organisationId ||
        !payload?.organisationCode
      ) {
        throw new Error(
          'The registration service did not return the organisation details.'
        );
      }

      saveAppSettings(
        {
          ...appSettings,
          operational: {
            ...appSettings.operational,
            baseAirfield: homeAirfield,
          },
        },
        payload.organisationId
      );

      setCreatedCode(payload.organisationCode);
      setCreatedEmail(email);
      setCreatedSubscriptionTier(
        form.subscriptionTier
      );
      setCreatedPassword(form.password);

      setCreatedUserId(payload.userId);
      setCreatedOrganisationId(
        payload.organisationId
      );

      setCreatedHomeAirfield(
        homeAirfield
      );

      if (onOwnerCreated) {
        onOwnerCreated();
      }
    } catch (error) {
      setSubmitError(
        error.message ||
          'Registration failed.'
      );
    } finally {
      setLoading(false);
    }
  }

  async function handleContinue() {
    setVerifyMessage('');
    setLoginLoading(true);

    try {
      const {
        data: loginData,
        error: loginError,
      } =
        await supabase.auth.signInWithPassword({
          email: createdEmail,
          password: createdPassword,
        });

      if (loginError) {
        setVerifyMessage(
          'Please verify your email before continuing, then try again.'
        );

        return;
      }

      if (!loginData?.user?.id) {
        setVerifyMessage(
          'Could not identify your owner account. Please try logging in again.'
        );

        return;
      }

      if (loginData.user.id !== createdUserId) {
        setVerifyMessage(
          'The signed-in account does not match the owner account created. Please log in with the registration email.'
        );

        return;
      }

      await saveOrganisationBaseAirfield(
        createdOrganisationId,
        createdHomeAirfield
      );

      await saveOrganisationPackage(
        loginData.session?.access_token,
        createdSubscriptionTier
      );
      await sendAccountActivityNotification(
        loginData.session?.access_token,
        'account_created'
      );
      navigate('/dashboard');
    } catch (error) {
      setVerifyMessage(
        error.message ||
          'Could not complete the organisation setup. Please try again.'
      );
    } finally {
      setLoginLoading(false);
    }
  }

  if (createdCode) {
    return (
      <PublicShell
        rightActionLabel="Log in"
        onRightAction={() =>
          navigate('/login')
        }
      >
        <main style={styles.hero}>
          <h1 style={styles.title}>
            Organisation created.
          </h1>

          <p style={styles.subtitle}>
            Your organisation is ready. Share
            this code with members you want to
            invite.
          </p>

          <div style={styles.panel}>
            <div style={styles.successBox}>
              <div style={styles.successTitle}>
                Account email
              </div>

              <div style={styles.successText}>
                {createdEmail}
              </div>
            </div>

            <div style={styles.codeBox}>
              <div style={styles.codeLabel}>
                Organisation code
              </div>

              <div style={styles.code}>
                {createdCode}
              </div>
            </div>

            <div
              style={styles.packageSummary}
            >
              <span
                style={styles.packageSummaryLabel}
              >
                Selected package:
              </span>{' '}

              <span
                style={styles.packageSummaryValue}
              >
                {getSubscriptionTierLabel(
                  createdSubscriptionTier
                )}
              </span>
            </div>

            <p style={styles.helpText}>
              Members should choose{' '}
              <strong>
                Join organisation
              </strong>{' '}
              and enter this code.
            </p>

            <button
              type="button"
              onClick={handleContinue}
              style={{
                ...styles.primaryButton,
                ...(loginLoading
                  ? styles.disabledButton
                  : {}),
              }}
              disabled={loginLoading}
            >
              {loginLoading
                ? 'Completing setup...'
                : 'Continue'}
            </button>

            {verifyMessage && (
              <div style={styles.error}>
                {verifyMessage}
              </div>
            )}
          </div>
        </main>
      </PublicShell>
    );
  }

  return (
    <PublicShell
      rightActionLabel="Log in"
      onRightAction={() =>
        navigate('/login')
      }
    >
      <main style={styles.hero}>
        <h1 style={styles.title}>
          Create organisation.
        </h1>

        <p style={styles.subtitle}>
          Set up your club, school,
          syndicate or private group. You
          will become the owner and can
          invite members afterwards.
        </p>

        <div style={styles.panel}>
          <form
            onSubmit={handleSubmit}
            autoComplete="off"
            style={styles.form}
          >
            <Field
              label="Organisation name"
              error={errors.orgName}
            >
              <input
                name="orgName"
                placeholder="e.g. Elstree Flyers"
                value={form.orgName}
                onChange={handleChange}
                style={inputStyle(
                  errors.orgName
                )}
              />
            </Field>

            <Field
              label="Your full name"
              error={errors.fullName}
            >
              <input
                name="fullName"
                placeholder="e.g. Bill Watson"
                value={form.fullName}
                onChange={handleChange}
                style={inputStyle(
                  errors.fullName
                )}
              />
            </Field>

            <Field
              label="Email address"
              error={errors.email}
            >
              <input
                name="email"
                type="email"
                placeholder="you@example.com"
                value={form.email}
                onChange={handleChange}
                style={inputStyle(
                  errors.email
                )}
              />
            </Field>

            <Field
              label="Password"
              error={errors.password}
            >
              <input
                name="password"
                type="password"
                placeholder="Create a password"
                value={form.password}
                onChange={handleChange}
                style={inputStyle(
                  errors.password
                )}
              />
            </Field>

            <Field
              label="Confirm password"
              error={
                errors.confirmPassword
              }
            >
              <input
                name="confirmPassword"
                type="password"
                placeholder="Confirm your password"
                value={
                  form.confirmPassword
                }
                onChange={handleChange}
                style={inputStyle(
                  errors.confirmPassword
                )}
              />
            </Field>

            <Field
              label="Phone number"
              error={errors.phone}
            >
              <input
                name="phone"
                type="tel"
                placeholder="Contact number"
                value={form.phone}
                onChange={handleChange}
                style={inputStyle(
                  errors.phone
                )}
              />
            </Field>

            <Field
              label="Base airfield"
              error={
                errors.homeAirfield
              }
            >
              <input
                name="homeAirfield"
                placeholder="e.g. EGTR"
                value={
                  form.homeAirfield
                }
                onChange={handleChange}
                style={inputStyle(
                  errors.homeAirfield
                )}
              />
            </Field>

            <div
              style={styles.packageSection}
            >
              <div style={styles.label}>
                Choose your Wingit package
              </div>

              <label
                style={styles.packageOption}
              >
                <input
                  type="radio"
                  name="subscriptionTier"
                  value="calendar"
                  checked={
                    form.subscriptionTier ===
                    'calendar'
                  }
                  onChange={handleChange}
                />

                <span
                  style={styles.packageText}
                >
                  <span
                    style={styles.packageTitle}
                  >
                    Calendar booking
                  </span>

                  <span
                    style={styles.packagePrice}
                  >
                    Free
                  </span>

                  <span
                    style={styles.packageDescription}
                  >
                    Aircraft calendar, booking management and pre-flight workflow.
                  </span>

                  <span
                    style={styles.packageNote}
                  >
                    Includes 30 days of Full Package access when your organisation is created.
                  </span>
                </span>
              </label>

              <label
                style={styles.packageOption}
              >
                <input
                  type="radio"
                  name="subscriptionTier"
                  value="calendar-logbook-techlog"
                  checked={
                    form.subscriptionTier ===
                    'calendar-logbook-techlog'
                  }
                  onChange={handleChange}
                />

                <span
                  style={styles.packageText}
                >
                  <span
                    style={styles.packageTitle}
                  >
                    Calendar + logbooks
                  </span>

                  <span
                    style={styles.packagePrice}
                  >
                    £19.99/month
                  </span>

                  <span
                    style={styles.packageDescription}
                  >
                    Calendar booking, pre-flight, aircraft tech log and pilot logbook.
                  </span>

                  <span
                    style={styles.packageNote}
                  >
                    Includes up to 20 active members.
                  </span>

                  <span
                    style={styles.packageNote}
                  >
                    Additional active members are 50p each per month.
                  </span>

                  <span
                    style={styles.packageNote}
                  >
                    30-day Full Package trial included.
                  </span>
                </span>
              </label>

              <label
                style={styles.packageOption}
              >
                <input
                  type="radio"
                  name="subscriptionTier"
                  value="full-package"
                  checked={
                    form.subscriptionTier ===
                    'full-package'
                  }
                  onChange={handleChange}
                />

                <span
                  style={styles.packageText}
                >
                  <span
                    style={styles.packageTitle}
                  >
                    Full package
                  </span>

                  <span
                    style={styles.packagePrice}
                  >
                    £29.99/month
                  </span>

                  <span
                    style={styles.packageDescription}
                  >
                    Calendar booking, pre-flight, aircraft tech log, pilot logbook and payment integration.
                  </span>

                  <span
                    style={styles.packageNote}
                  >
                    Includes up to 20 active members.
                  </span>

                  <span
                    style={styles.packageNote}
                  >
                    Additional active members are 50p each per month.
                  </span>

                  <span
                    style={styles.packageNote}
                  >
                    30-day free trial.
                  </span>

                  <span
                    style={styles.packageNote}
                  >
                    Stripe processing charges apply to payments taken.
                  </span>

                  <span
                    style={styles.packageNote}
                  >
                    Wingit does not take a percentage of club membership payments.
                  </span>
                </span>
              </label>

              {errors.subscriptionTier && (
                <div
                  style={styles.fieldError}
                >
                  {
                    errors.subscriptionTier
                  }
                </div>
              )}

              <p
                style={styles.packageTrialCaption}
              >
                No payment card is required to begin the 30-day trial. Before it ends, you can confirm your selected package, choose another package, or continue with Calendar booking free.
              </p>
            </div>

            <label
              style={styles.checkboxLabel}
            >
              <input
                type="checkbox"
                name="agree"
                checked={form.agree}
                onChange={handleChange}
                style={{
                  marginRight: 8,
                }}
              />

              I agree to the Terms of
              Service and Privacy Policy
            </label>

            {errors.agree && (
              <div style={styles.fieldError}>
                {errors.agree}
              </div>
            )}

            {submitError && (
              <div style={styles.error}>
                {submitError}
              </div>
            )}

            <button
              type="submit"
              style={{
                ...styles.primaryButton,
                ...(loading
                  ? styles.disabledButton
                  : {}),
              }}
              disabled={loading}
            >
              {loading
                ? 'Creating...'
                : 'Create organisation'}
            </button>
          </form>
        </div>
      </main>
    </PublicShell>
  );
}

function PublicShell({
  children,
  rightActionLabel,
  onRightAction,
}) {
  return (
    <div style={styles.page}>
      {rightActionLabel && (
        <div style={styles.topBar}>
          <button
            type="button"
            onClick={onRightAction}
            style={styles.topButton}
          >
            {rightActionLabel}
          </button>
        </div>
      )}

      {children}
    </div>
  );
}

function Field({
  label,
  error,
  children,
}) {
  return (
    <label style={styles.field}>
      <span style={styles.label}>
        {label}
      </span>

      {children}

      {error && (
        <div style={styles.fieldError}>
          {error}
        </div>
      )}
    </label>
  );
}

function inputStyle(error) {
  return {
    ...styles.input,
    borderColor: error
      ? 'rgba(239,107,115,0.7)'
      : 'rgba(85,199,255,0.2)',
  };
}

const styles = {
  page: {
    minHeight: '100vh',
    background:
      'radial-gradient(circle at top, rgba(85,199,255,0.08), transparent 34%), linear-gradient(180deg, #06111f, #050914 70%)',
    color: '#ffffff',
    padding: '1.25rem',
  },

  topBar: {
    maxWidth: 980,
    margin: '0 auto',
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
  },

  topButton: {
    border:
      '1px solid rgba(85,199,255,0.24)',
    background:
      'rgba(255,255,255,0.025)',
    color: '#d7ebff',
    borderRadius: 10,
    padding: '0.55rem 0.9rem',
    fontWeight: 700,
    cursor: 'pointer',
  },

  hero: {
    maxWidth: 620,
    margin: '4.2rem auto 0',
    textAlign: 'center',
  },

  title: {
    margin: 0,
    fontSize: '2.4rem',
    fontWeight: 800,
    letterSpacing: '-0.04em',
  },

  subtitle: {
    maxWidth: 580,
    margin: '1rem auto 0',
    color: '#b6cbe3',
    fontSize: '1.05rem',
    lineHeight: 1.6,
  },

  panel: {
    marginTop: '2.4rem',
    textAlign: 'left',
    padding: '1.5rem',
    borderRadius: 14,
    background: '#0b1828',
    border:
      '1px solid rgba(85,199,255,0.14)',
  },

  form: {
    display: 'grid',
    gap: '0.9rem',
  },

  field: {
    display: 'grid',
    gap: '0.35rem',
  },

  label: {
    color: '#d7ebff',
    fontSize: '0.82rem',
    fontWeight: 700,
  },

  input: {
    width: '100%',
    boxSizing: 'border-box',
    border:
      '1px solid rgba(85,199,255,0.2)',
    background:
      'rgba(255,255,255,0.04)',
    color: '#ffffff',
    borderRadius: 10,
    padding: '0.75rem 0.85rem',
    fontSize: '0.98rem',
    outline: 'none',
  },

  checkboxLabel: {
    color: '#9fb6d1',
    fontSize: '0.92rem',
    lineHeight: 1.5,
  },

  fieldError: {
    color: '#ffb3b3',
    fontSize: '0.82rem',
  },

  error: {
    color: '#ffb3b3',
    background:
      'rgba(255,77,77,0.08)',
    border:
      '1px solid rgba(255,77,77,0.18)',
    borderRadius: 10,
    padding: '0.75rem',
    marginTop: '0.75rem',
  },

  primaryButton: {
    border: 'none',
    background: '#55c7ff',
    color: '#04111f',
    borderRadius: 10,
    padding: '0.75rem 1rem',
    fontWeight: 800,
    cursor: 'pointer',
    marginTop: '0.3rem',
    width: '100%',
  },

  disabledButton: {
    opacity: 0.6,
    cursor: 'wait',
  },

  successBox: {
    background:
      'rgba(255,255,255,0.04)',
    border:
      '1px solid rgba(255,255,255,0.08)',
    borderRadius: 10,
    padding: '0.85rem',
    marginBottom: '1rem',
  },

  successTitle: {
    color: '#8fa8c5',
    fontSize: '0.82rem',
    fontWeight: 700,
    marginBottom: 4,
  },

  successText: {
    color: '#ffffff',
    fontWeight: 800,
  },

  codeBox: {
    background: '#06111f',
    border:
      '1px solid rgba(85,199,255,0.18)',
    borderRadius: 10,
    padding: '1rem',
    marginBottom: '1rem',
    textAlign: 'center',
  },

  codeLabel: {
    color: '#8db7e6',
    fontSize: '0.82rem',
    fontWeight: 700,
    marginBottom: 6,
  },

  code: {
    color: '#55c7ff',
    fontSize: '1.55rem',
    fontWeight: 800,
    letterSpacing: '0.05em',
  },

  helpText: {
    color: '#9fb6d1',
    lineHeight: 1.5,
    margin: '0 0 1rem',
  },

  packageSection: {
    display: 'grid',
    gap: '0.55rem',
  },

  packageOption: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '0.6rem',
    color: '#d7ebff',
    background:
      'rgba(255,255,255,0.025)',
    border:
      '1px solid rgba(85,199,255,0.14)',
    borderRadius: 10,
    padding: '0.65rem 0.75rem',
    cursor: 'pointer',
  },

  packageText: {
    display: 'grid',
    gap: '0.12rem',
  },

  packageTitle: {
    fontSize: '0.94rem',
    fontWeight: 700,
    color: '#e8f4ff',
  },

  packagePrice: {
    fontSize: '0.9rem',
    fontWeight: 700,
    color: '#cce8ff',
  },

  packageDescription: {
    fontSize: '0.86rem',
    lineHeight: 1.45,
    color: '#9fb6d1',
  },

  packageNote: {
    fontSize: '0.8rem',
    lineHeight: 1.4,
    color: '#88a6c6',
  },

  packageTrialCaption: {
    margin: '0.25rem 0 0',
    color: '#8fa8c5',
    fontSize: '0.8rem',
    lineHeight: 1.45,
  },

  packageSummary: {
    color: '#9fb6d1',
    fontSize: '0.88rem',
    margin: '-0.4rem 0 1rem',
  },

  packageSummaryLabel: {
    color: '#8fa8c5',
  },

  packageSummaryValue: {
    color: '#d7ebff',
    fontWeight: 700,
  },
};